import { useState, useMemo } from "react";
import { Icon } from "@/components/Icon/Icon";
import type { SubstanceRow } from "./_model";
import { FAMILY_LABELS, FAMILY_COLORS, needsAttention } from "./_model";
import { defaultFilters } from "./filters";
import type { SubstanceFilters } from "./filters";

// ── SlFilterSection ─────────────────────────────────────────────────────────

interface FilterSectionProps {
  title: string;
  icon: string;
  count?: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
  tone?: string;
}

function FilterSection({ title, icon, count, children, defaultOpen = true, tone }: FilterSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: "1px solid var(--line)" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 8,
          padding: "9px 14px", background: "transparent",
        }}
      >
        <span style={{ display: "inline-flex", color: tone ?? "var(--ink-3)" }}>
          <Icon name={icon as Parameters<typeof Icon>[0]["name"]} size={13} />
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
          color: "var(--ink-2)", flex: 1, textAlign: "left",
        }}>
          {title}
        </span>
        {typeof count === "number" && (
          <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>{count}</span>
        )}
        <span style={{
          color: "var(--ink-4)", display: "inline-flex",
          transform: open ? "none" : "rotate(-90deg)", transition: "transform 140ms",
        }}>
          <Icon name="chevron" size={11} />
        </span>
      </button>
      {open && <div className="vstack" style={{ paddingBottom: 6, gap: 0 }}>{children}</div>}
    </div>
  );
}

// ── SlFacet ──────────────────────────────────────────────────────────────────

interface FacetProps {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  dot?: string;
  badge?: React.ReactNode;
}

function Facet({ active, onClick, label, count, dot, badge }: FacetProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 8,
        padding: "5px 14px 5px 30px", border: "none", borderRadius: 0,
        boxShadow: active ? "inset 3px 0 0 var(--brand-green)" : "none",
        background: active ? "var(--accent-soft)" : "transparent",
        color: active ? "var(--accent-ink)" : "var(--ink-2)", textAlign: "left",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--panel)"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
    >
      {dot && (
        <span style={{ width: 8, height: 8, borderRadius: 2, background: dot, flexShrink: 0 }} />
      )}
      <span style={{ fontSize: 12, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      {badge}
      <span className="mono" style={{ fontSize: 10, color: active ? "var(--accent-ink)" : "var(--ink-4)" }}>
        {count}
      </span>
    </button>
  );
}

// ── SubstanceFilterRail ───────────────────────────────────────────────────────

interface SubstanceFilterRailProps {
  items: SubstanceRow[];
  filters: SubstanceFilters;
  setFilters: React.Dispatch<React.SetStateAction<SubstanceFilters>>;
}

export function SubstanceFilterRail({ items, filters, setFilters }: SubstanceFilterRailProps) {
  const c = (pred: (s: SubstanceRow) => boolean) => items.filter(pred).length;

  const toggle = (key: keyof SubstanceFilters, val: string) =>
    setFilters((f) => {
      const cur = new Set(f[key] as string[]);
      cur.has(val) ? cur.delete(val) : cur.add(val);
      return { ...f, [key]: [...cur] };
    });

  const has = (key: keyof SubstanceFilters, val: string): boolean =>
    (filters[key] as string[]).includes(val);

  // single-select fields only (i.e. `stock`); never use on an array filter like `status`
  const single = (key: keyof SubstanceFilters, val: string) =>
    setFilters((f) => ({ ...f, [key]: (f[key] as string | null) === val ? null : val }));

  const families = useMemo(() => {
    // Derive the family facets from the rows actually present, so custom families
    // (not in the fixed palette) appear with their real label/color.
    const present = new Map<string, { slug: string; label: string; color: string | null }>();
    for (const s of items) {
      if (s.family && !present.has(s.family)) {
        present.set(s.family, {
          slug: s.family,
          label: s.familyLabel ?? FAMILY_LABELS[s.family] ?? s.family,
          color: s.familyColor ?? FAMILY_COLORS[s.family] ?? null,
        });
      }
    }
    const order = Object.keys(FAMILY_LABELS);
    return [...present.values()].sort((a, b) => {
      const ai = order.indexOf(a.slug);
      const bi = order.indexOf(b.slug);
      if (ai !== -1 && bi !== -1) return ai - bi; // standard families keep curated order
      if (ai !== -1) return -1; // standard before custom
      if (bi !== -1) return 1;
      return a.label.localeCompare(b.label); // custom alpha
    });
  }, [items]);

  const tagsInUse = useMemo(() => {
    const set = new Set<string>();
    items.forEach((s) => (s.tags ?? []).forEach((t) => set.add(t)));
    return [...set].sort();
  }, [items]);

  const needsCount = c(needsAttention);

  const anyActive =
    filters.type.length > 0 ||
    filters.status.length > 0 ||
    filters.comp.length > 0 ||
    filters.stock !== null ||
    filters.family.length > 0 ||
    filters.tag.length > 0;

  return (
    <aside style={{
      width: 250, flexShrink: 0, background: "var(--bg-sunk)",
      borderRight: "1px solid var(--line)", display: "flex", flexDirection: "column",
      overflow: "auto",
    }}>
      <div>
        <div className="hstack" style={{
          justifyContent: "space-between", padding: "12px 14px",
          borderBottom: "1px solid var(--line)",
        }}>
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", color: "var(--ink-3)",
          }}>
            Filters
          </span>
          {anyActive && (
            <button
              type="button"
              onClick={() => setFilters(defaultFilters())}
              className="hstack"
              style={{ gap: 4, fontSize: 11, color: "var(--ink-4)" }}
            >
              <Icon name="reset" size={10} />Reset
            </button>
          )}
        </div>

        {needsCount > 0 && (
          <button
            type="button"
            onClick={() => toggle("status", "NOT_AVAILABLE")}
            style={{
              width: "100%", padding: "10px 14px", textAlign: "left",
              borderBottom: "1px solid var(--line)",
              boxShadow: has("status", "NOT_AVAILABLE") ? "inset 3px 0 0 #c9a93b" : "none",
              background: "var(--signal-soft)", color: "#7c5813",
              display: "flex", alignItems: "flex-start", gap: 9, cursor: "pointer",
            }}
          >
            <span style={{ marginTop: 1 }}><Icon name="info" size={14} /></span>
            <span className="vstack" style={{ gap: 2, lineHeight: 1.35 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{needsCount} need composition data</span>
              <span style={{ fontSize: 11 }}>Add a chromatogram or formula to use in N-OS</span>
            </span>
          </button>
        )}

        <FilterSection title="Substance type" icon="molecule" count={items.length}>
          <Facet
            label="Compounds" count={c((s) => s.type === "compound")}
            active={has("type", "compound")} onClick={() => toggle("type", "compound")}
          />
          <Facet
            label="Mixtures" count={c((s) => s.type === "mixture")}
            active={has("type", "mixture")} onClick={() => toggle("type", "mixture")}
          />
        </FilterSection>

        <FilterSection title="Readiness" icon="zap">
          <Facet
            label="Ready" count={c((s) => s.status === "READY")}
            dot="var(--brand-lime)" active={has("status", "READY")} onClick={() => toggle("status", "READY")}
          />
          <Facet
            label="Analyzing" count={c((s) => s.status === "ANALYZING")}
            dot="#7d93a1" active={has("status", "ANALYZING")} onClick={() => toggle("status", "ANALYZING")}
          />
          <Facet
            label="Re-analyzing" count={c((s) => s.status === "RE-ANALYZING")}
            dot="#7d93a1" active={has("status", "RE-ANALYZING")} onClick={() => toggle("status", "RE-ANALYZING")}
          />
          <Facet
            label="Needs composition" count={c((s) => s.status === "NOT_AVAILABLE")}
            dot="#c9a93b" active={has("status", "NOT_AVAILABLE")} onClick={() => toggle("status", "NOT_AVAILABLE")}
          />
        </FilterSection>

        <FilterSection title="Composition data" icon="chromatogram">
          <Facet
            label="Has chromatogram" count={c((s) => s.chrom > 0)}
            active={has("comp", "chrom")} onClick={() => toggle("comp", "chrom")}
          />
          <Facet
            label="Has formulation" count={c((s) => !!s.formula)}
            active={has("comp", "formula")} onClick={() => toggle("comp", "formula")}
          />
          <Facet
            label="None yet (mixture)" count={c((s) => s.type === "mixture" && !s.chrom && !s.formula)}
            active={has("comp", "none")} onClick={() => toggle("comp", "none")}
          />
        </FilterSection>

        <FilterSection title="Inventory" icon="box" defaultOpen={false}>
          <Facet
            label="In stock" count={c((s) => !!(s.stock && s.stock.inStock))}
            dot="var(--brand-lime)" active={filters.stock === "in"} onClick={() => single("stock", "in")}
          />
          <Facet
            label="Out of stock" count={c((s) => !!(s.stock && !s.stock.inStock))}
            dot="var(--line-strong)" active={filters.stock === "out"} onClick={() => single("stock", "out")}
          />
          <Facet
            label="No inventory info" count={c((s) => !s.stock)}
            active={filters.stock === "noinfo"} onClick={() => single("stock", "noinfo")}
          />
        </FilterSection>

        <FilterSection title="Aroma family" icon="sparkle" defaultOpen={false}>
          {families.map((f) => (
            <Facet
              key={f.slug}
              label={f.label}
              dot={f.color ?? undefined}
              count={c((s) => s.family === f.slug)}
              active={has("family", f.slug)}
              onClick={() => toggle("family", f.slug)}
            />
          ))}
        </FilterSection>

        {tagsInUse.length > 0 && (
          <FilterSection title="Tags" icon="bookmark" defaultOpen={false}>
            {tagsInUse.map((t) => (
              <Facet
                key={t}
                label={t}
                count={c((s) => (s.tags ?? []).includes(t))}
                active={has("tag", t)}
                onClick={() => toggle("tag", t)}
              />
            ))}
          </FilterSection>
        )}
      </div>
    </aside>
  );
}
